import {createContext, memo, useCallback, useMemo, useRef} from 'react'

import {apiGetRepositories} from '../../api/repository/api-get-repositories'
import type {GetSuggestedRepositoriesResponse, SuggestedRepository} from '../../api/repository/contracts'
import {useProjectNumber} from '../memex/use-project-number'

export const FETCH_REPOS_CACHE_TIMEOUT = 30_000

export type RepositoriesContextType = {
  /** A callback to fire to fetch suggested repositories for the current project */
  suggestRepositories: (repositoryId?: number) => Promise<Array<SuggestedRepository>>
  /**
   * A reference to the current set of repositories stored in the provider.
   *
   * WARNING: this is only used for tests currently, and the application should
   * invoke `suggestRepositories` which handles refreshing or returning the
   * cached values.
   */
  repositoriesRef: React.MutableRefObject<Array<SuggestedRepository>>
  /**
   * Nulls out the lastFetchedRepositoriesAt timestamp, which invalidates
   * the cache and ensures suggestions are fetched from the server
   * when they're next requested
   */
  clearCachedSuggestions: () => void
}

export const RepositoriesContext = createContext<RepositoriesContextType | null>(null)

export const RepositoriesStateProvider = memo<{children: React.ReactNode}>(function RepositoriesStateProvider({
  children,
}) {
  const repositoriesRef = useRef<Array<SuggestedRepository>>([])

  const {projectNumber} = useProjectNumber()

  const lastFetchedRepositoriesAt = useRef<number | null>(null)
  const fetchRepositoriesPromise = useRef<Promise<GetSuggestedRepositoriesResponse> | null>(null)

  const checkShouldFetchRepositories = useCallback((repositoryId: number | undefined): boolean => {
    if (repositoryId && !repositoriesRef.current.find(repo => repo.id === repositoryId)) return true
    const now = Date.now()
    const lastFetched = lastFetchedRepositoriesAt.current
    const shouldFetch = lastFetched == null || now - lastFetched > FETCH_REPOS_CACHE_TIMEOUT
    if (shouldFetch) lastFetchedRepositoriesAt.current = now
    return shouldFetch
  }, [])

  const suggestRepositories = useCallback(
    async (repositoryId: number | undefined) => {
      if (!checkShouldFetchRepositories(repositoryId)) {
        if (fetchRepositoriesPromise.current) {
          const result = await fetchRepositoriesPromise.current
          return result.repositories
        }

        if (repositoriesRef.current.length > 0) {
          return repositoriesRef.current
        }
      }

      fetchRepositoriesPromise.current = apiGetRepositories(projectNumber, repositoryId)
      const response = await fetchRepositoriesPromise.current

      repositoriesRef.current = response.repositories

      fetchRepositoriesPromise.current = null

      return response.repositories
    },
    [checkShouldFetchRepositories, projectNumber],
  )

  const clearLastFetchedRepositoriesAt = useCallback(() => {
    lastFetchedRepositoriesAt.current = null
  }, [])

  const contextValue: RepositoriesContextType = useMemo<RepositoriesContextType>(
    () => ({
      suggestRepositories,
      repositoriesRef,
      clearCachedSuggestions: clearLastFetchedRepositoriesAt,
    }),
    [suggestRepositories, clearLastFetchedRepositoriesAt],
  )

  return <RepositoriesContext.Provider value={contextValue}>{children}</RepositoriesContext.Provider>
})

try{ RepositoriesContext.displayName ||= 'RepositoriesContext' } catch {}
try{ RepositoriesStateProvider.displayName ||= 'RepositoriesStateProvider' } catch {}